import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import templatesService from '../../../../services/templates.service';
import { useEyeconxStore } from '../../../../store/eyeconxStore';
import { showError, showSuccess } from '../../../../utils/ecxSwal';
import { ClinicInfoDetailItem, ClinicInfoPanel } from '../../settings/clinicsettings/info/clinicInfoParts';
import ClinicSettingsCancelButton from '../../settings/clinicsettings/subparts/ClinicSettingsCancelButton';
import ClinicSettingsEmpty from '../../settings/clinicsettings/subparts/ClinicSettingsEmpty';
import ClinicSettingsFormFooter from '../../settings/clinicsettings/subparts/ClinicSettingsFormFooter';
import ClinicSettingsListSkeleton from '../../settings/clinicsettings/subparts/ClinicSettingsListSkeleton';
import TemplateTypeList from '../TemplateTypeList';
import { formatReminderLabel, groupTemplatesByType } from '../templateTypeGroups';
import SmsPhonePreview from './SmsPhonePreview';

const PLACEHOLDERS = [
    'PATIENT_NAME',
    'DATE',
    'TIME',
    'CL_ORDER_LINK',
    'SURVEY_LINK',
    'THANK_YOU_LINK',
];

const SmsTemplates = () => {
    const clinicId = useEyeconxStore((state) => state.user.clinicId);

    const [listLoading, setListLoading] = useState(true);
    const [detailLoading, setDetailLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [templates, setTemplates] = useState([]);
    const [selectedId, setSelectedId] = useState(null);
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const [editing, setEditing] = useState(false);
    const hasAutoSelected = useRef(false);

    const { register, handleSubmit, reset, watch } = useForm({
        defaultValues: {
            content: '',
            status: false,
            reminderTypeId: null,
        },
    });

    const contentValue = watch('content', '');
    const statusValue = watch('status');
    const wordCount = contentValue?.trim() ? contentValue.trim().split(/\s+/).length : 0;
    const characterCount = contentValue?.length || 0;

    const loadTemplateById = useCallback(
        async (id) => {
            if (!id || !clinicId) return;

            setSelectedId(id);
            setEditing(false);
            setDetailLoading(true);

            try {
                const res = await templatesService.getSmsTemplateById(clinicId, id);
                const data = res.data;
                setSelectedTemplate(data);
                reset({
                    id: data.id,
                    content: data.content || '',
                    status: Boolean(data.status),
                    reminderTypeId: data.reminderTypeId,
                });
            } catch (err) {
                console.error(err);
                showError({
                    title: 'Load Failed',
                    text: 'Unable to load this SMS template. Please try again.',
                });
            } finally {
                setDetailLoading(false);
            }
        },
        [clinicId, reset]
    );

    const loadTemplates = useCallback(async () => {
        if (!clinicId) return;

        setListLoading(true);
        try {
            const res = await templatesService.getSmsTemplates(clinicId);
            const items = res.data || [];
            setTemplates(items);

            if (items.length > 0 && !hasAutoSelected.current) {
                hasAutoSelected.current = true;
                const firstTemplate = groupTemplatesByType(items)[0]?.templates[0];
                if (firstTemplate) {
                    await loadTemplateById(firstTemplate.id);
                }
            }
        } catch (err) {
            console.error(err);
            setTemplates([]);
            showError({
                title: 'Load Failed',
                text: 'Unable to load SMS templates. Please try again.',
            });
        } finally {
            setListLoading(false);
        }
    }, [clinicId, loadTemplateById]);

    useEffect(() => {
        hasAutoSelected.current = false;
        loadTemplates();
    }, [loadTemplates]);

    const handleCancelEdit = () => {
        setEditing(false);
        if (selectedId) {
            loadTemplateById(selectedId);
        }
    };

    const onSubmit = async (data) => {
        setIsSaving(true);
        try {
            await templatesService.updateSmsTemplate(clinicId, selectedId, {
                id: data.id,
                clinicId,
                content: data.content,
                status: data.status,
                reminderTypeId: data.reminderTypeId,
            });

            await showSuccess({
                title: 'Template Updated',
                text: 'The SMS template was saved successfully.',
            });
            setEditing(false);
            await loadTemplates();
            await loadTemplateById(selectedId);
        } catch (err) {
            console.error(err);
            showError({
                title: 'Update Failed',
                text: 'Unable to update the SMS template. Please try again.',
            });
        } finally {
            setIsSaving(false);
        }
    };

    const detailActions =
        selectedId && !editing ? (
            <button
                type="button"
                className="btn btn-sm btn-light-primary rounded-pill"
                onClick={() => setEditing(true)}
                disabled={detailLoading || !selectedTemplate}
            >
                <i className="bi bi-pencil me-1"></i>
                Edit
            </button>
        ) : editing ? (
            <ClinicSettingsCancelButton onClick={handleCancelEdit} disabled={isSaving} />
        ) : null;

    return (
        <div className="row g-3 templates-layout">
            <div className="col-12 col-lg-3 templates-list-sidebar">
                <ClinicInfoPanel
                    title="Template types"
                    icon="bi-list-ul"
                    actions={
                        !listLoading && templates.length > 0 ? (
                            <span className="ecx-page-chip">
                                <i className="bi bi-chat-left-text"></i>
                                {templates.length}
                            </span>
                        ) : null
                    }
                >
                    {listLoading ? (
                        <ClinicSettingsListSkeleton rows={5} />
                    ) : templates.length === 0 ? (
                        <ClinicSettingsEmpty
                            icon="bi-chat-left-text"
                            title="No SMS templates"
                            description="No SMS templates are configured for this clinic."
                        />
                    ) : (
                        <TemplateTypeList
                            templates={templates}
                            selectedId={selectedId}
                            onSelect={loadTemplateById}
                            isSaving={isSaving}
                            itemIcon="bi-chat-left-text"
                        />
                    )}
                </ClinicInfoPanel>
            </div>

            <div className="col-12 col-lg-9">
                {!selectedId ? (
                    <ClinicSettingsEmpty
                        icon="bi-chat-left-text"
                        title="Select a template"
                        description="Choose a template type from the list to view or edit its content."
                    />
                ) : detailLoading ? (
                    <ClinicInfoPanel title="Loading template…" icon="bi-chat-left-text">
                        <ClinicSettingsListSkeleton rows={4} />
                    </ClinicInfoPanel>
                ) : (
                    <form onSubmit={handleSubmit(onSubmit)} className="h-100">
                        <ClinicInfoPanel
                            title={formatReminderLabel(selectedTemplate?.reminderName) || 'SMS template'}
                            icon="bi-chat-left-text"
                            actions={detailActions}
                        >
                            <div className="row g-4">
                                <div className="col-lg-7 d-flex flex-column">
                                    <div className="templates-detail-meta">
                                        <ClinicInfoDetailItem label="Template name">
                                            {formatReminderLabel(selectedTemplate?.reminderName)}
                                        </ClinicInfoDetailItem>

                                        <div className="templates-detail-meta__status">
                                            <span className="templates-detail-meta__status-label">
                                                Status
                                            </span>
                                            <div className="templates-detail-meta__status-value">
                                                {editing ? (
                                                    <div className="form-check form-switch mb-0">
                                                        <input
                                                            type="checkbox"
                                                            className="form-check-input"
                                                            disabled={isSaving}
                                                            {...register('status')}
                                                        />
                                                        <label className="form-check-label">
                                                            {statusValue ? 'Active' : 'Inactive'}
                                                        </label>
                                                    </div>
                                                ) : (
                                                    <span
                                                        className={`clinic-settings-status-pill ${
                                                            statusValue ? 'is-open' : 'is-closed'
                                                        }`}
                                                    >
                                                        {statusValue ? 'Active' : 'Inactive'}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    <div className="templates-field-group">
                                        <div className="templates-field-group__header">
                                            <label className="templates-field-group__label">
                                                SMS content
                                            </label>
                                            <span className="templates-field-group__hint">
                                                {wordCount} words · {characterCount} characters
                                            </span>
                                        </div>
                                        <textarea
                                            rows={8}
                                            className="form-control templates-field-group__input"
                                            disabled={!editing || isSaving}
                                            placeholder="Type your SMS template…"
                                            {...register('content')}
                                        />
                                        <div>
                                            <small className="text-muted d-block mb-2">
                                                Available placeholders
                                            </small>
                                            <div className="templates-placeholder-chips">
                                                {PLACEHOLDERS.map((placeholder) => (
                                                    <code key={placeholder}>{`{${placeholder}}`}</code>
                                                ))}
                                            </div>
                                        </div>
                                    </div>

                                    <div className="templates-detail-footer">
                                        <span className="templates-detail-footer__updated">
                                            <i className="bi bi-clock"></i>
                                            Last updated {selectedTemplate?.modifiedAt || '—'}
                                        </span>
                                        {editing && (
                                            <ClinicSettingsFormFooter
                                                isSaving={isSaving}
                                                submitLabel="Save Template"
                                                showCancel
                                                onCancel={handleCancelEdit}
                                            />
                                        )}
                                    </div>
                                </div>

                                <div className="col-lg-5">
                                    <div className="templates-preview-card">
                                        <div className="templates-preview-card__header">
                                            <i className="bi bi-phone"></i>
                                            Live SMS preview
                                        </div>
                                        <div className="templates-preview-card__body">
                                            <SmsPhonePreview message={contentValue} />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </ClinicInfoPanel>
                    </form>
                )}
            </div>
        </div>
    );
};

export default SmsTemplates;
